import { defineMessages, type IntlShape } from 'react-intl';
import type { CtaBlockExtender } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';
import type { BlockConfigBase, BlocksFormData, JSONSchema } from '@plone/types';
import addBlocksBg from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksBg';
import addBlocksFullWidth from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlocksFullWidth';
import { addCtaFieldset } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';
import addBlockLines from '@redturtle/qubicaamf-theme/components/Blocks/extenders/addBlockLines';

const messages = defineMessages({
  Tabs: {
    id: 'Tabs',
    defaultMessage: 'Tabs',
  },
  Title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  Item: {
    id: 'Item',
    defaultMessage: 'Item',
  },
  item_title: {
    id: 'item_title',
    defaultMessage: 'Item title',
  },
  tabs_style: {
    id: 'tabs_style',
    defaultMessage: 'Tabs style',
  },
});

export const PanelSchema = {
  title: 'Tabs',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['panel_title'],
    },
  ],
  properties: {
    panel_title: {
      title: 'Tabs title',
    },
  },
  required: [],
};

export interface TabsData extends CtaBlockExtender, JSONSchema, BlocksFormData {
  '@type': 'tabs';
  title: string;
  text?: object;
  items?: Array<{ '@id': string; title?: string; text?: object }>;
  allowedBlocks?: string[];
  tabs?: Array<{
    '@id': string;
    title?: string;
    blocks?: Record<string, object>;
    blocks_layout?: {
      items: string[];
    };
  }>;
  tabs_style?: 'simple' | 'buttons';
}

const TabsSchema = ({
  data,
  intl,
}: {
  data: TabsData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: 'Blocco Tabs',
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: ['title', 'tabs', 'tabs_style'],
      },
    ],
    properties: {
      title: {
        title: intl.formatMessage(messages.Title),
      },
      tabs: {
        title: intl.formatMessage(messages.Tabs),
        widget: 'object_list',
        schema: {
          title: intl.formatMessage(messages.Item),
          fieldsets: [
            {
              id: 'default',
              title: 'Default',
              fields: ['title'],
            },
          ],
          properties: {
            title: {
              title: intl.formatMessage(messages.item_title),
            },
          },
          required: ['title'],
        },
      },
      tabs_style: {
        title: intl.formatMessage(messages.tabs_style),
        widget: 'select',
        default: 'simple',
        choices: [
          ['simple', 'Simple'],
          ['buttons', 'Buttons'],
        ],
      },
    },
    required: [''],
  };

  addBlocksBg({ schema, formData: data, intl });
  addBlocksFullWidth({ schema, formData: data, intl });
  addCtaFieldset({ schema, intl });
  addBlockLines({ schema, formData: data, intl });

  return schema;
};

export default TabsSchema;

export interface TabsConfig extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof TabsSchema;
  allowedBlocks: string[];
}
